// src/components/ComponentDetailsList.js
import React from 'react';

const ComponentDetailsList = ({ components, partNumber, onViewDetails, loadingComponentId }) => {
  if (!components || components.length === 0) {
    return (
      <div className="p-4 text-sm text-gray-500">
        No components available for this part
      </div>
    );
  }

  const isNegativeMetric = (componentId) => {
    return componentId === 'churn_risk_indicator' || componentId === 'competitive_threat';
  };

  const getStatusIcon = (component) => {
    const componentId = component.componentId;
    const isNegative = isNegativeMetric(componentId);
    
    // Check if deferred based on component name
    if (component.componentName === 'KPI Achievement Assessment' || 
        component.componentName === 'Service Level Performance') {
      return <span className="text-gray-400 text-xl">⊗</span>;
    }
    
    // NEW LOGIC FIRST: Use meetsExpectation if available (polarity-based data)
    if (typeof component.meetsExpectation !== 'undefined') {
        return component.meetsExpectation 
            ? <span className="text-green-600 text-xl">✓</span>
            : <span className="text-red-500 text-xl">✗</span>;
    }

    // LEGACY FALLBACK: For old data without meetsExpectation
    // For NEGATIVE metrics (churn risk, competitive threat):
    // - NOT detected (present=false) = GOOD = green checkmark
    // - Detected (present=true) = BAD = red X
    if (isNegative) {
      if (!component.present) {
        // Not detected = good for negative metrics
        return <span className="text-green-600 text-xl">✓</span>;
      } else {
        // Detected = bad for negative metrics
        return <span className="text-red-500 text-xl">✗</span>;
      }
    }
    
    // For POSITIVE metrics (all others):
    // - Detected (present=true) = GOOD = green checkmark
    // - NOT detected (present=false) = gray X
    if (component.present) {
      return <span className="text-green-600 text-xl">✓</span>;
    }
    
    return <span className="text-gray-300 text-xl">✗</span>;
  };

  const getDeferredExplanation = (componentName) => {
    if (componentName === 'KPI Achievement Assessment') {
      return 'Not available in 0.7 (requires KPI dashboard infrastructure)';
    }
    if (componentName === 'Service Level Performance') {
      return 'Not available in 0.7 (requires SLA tracking infrastructure)';
    }
    return null;
  };

  const isComponentDeferred = (componentName) => {
    return componentName === 'KPI Achievement Assessment' || 
           componentName === 'Service Level Performance';
  };

  return (
    <div className="space-y-2 p-4">
      {components.map((component) => {
        const deferred = isComponentDeferred(component.componentName);
        const deferredExplanation = getDeferredExplanation(component.componentName);
        const isNegative = isNegativeMetric(component.componentId);
        // Use pre-calculated meetsExpectation if available, otherwise fallback to old logic
        const meetsExpectation = typeof component.meetsExpectation !== 'undefined' 
            ? component.meetsExpectation 
            : (isNegative ? !component.present : component.present);
        
        return (
          <div 
            key={component.componentId}
            className={`flex items-start gap-3 p-3 rounded-md ${
              deferred ? 'bg-gray-50' : 'bg-white border border-gray-200'
            }`}
          >
            {/* Status Icon */}
            <div className="flex-shrink-0 mt-0.5">
              {getStatusIcon(component)}
            </div>

            {/* Component Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <h5 className={`text-sm font-medium ${
                    deferred ? 'text-gray-500' : 'text-gray-900'
                  }`}>
                    {component.componentName}
                    {isNegative && (
                      <span className="ml-2 text-xs font-normal text-amber-600">
                        (Negative Metric)
                      </span>
                    )}
                    {/* Display Polarity Counts if available */}
                    {(component.positiveCount !== undefined || component.negativeCount !== undefined) && (
                        <span className="ml-2 text-xs font-normal text-gray-500">
                            ({component.positiveCount || 0}+ / {component.negativeCount || 0}-)
                      </span>
                    )}
                  </h5>
                  <p className={`text-xs mt-0.5 ${
                    deferred ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {component.weight}% of total score
                  </p>
                  
                  {/* Status explanation */}
                  {!deferred && (
                    <p className={`text-xs mt-1 ${
                      meetsExpectation ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {meetsExpectation 
                        ? '✓ Meets Expectation' 
                        : '✗ Attention Needed'}
                    </p>
                  )}
                  
                  {/* Deferred explanation */}
                  {deferred && deferredExplanation && (
                    <p className="text-xs text-gray-500 mt-2 italic">
                      {deferredExplanation}
                    </p>
                  )}
                </div>

                {/* View Details Link - only show for components with context/evidence */}
                {!deferred && component.present && component.context && component.context.length > 0 && (
                  <button
                    onClick={() => onViewDetails(component)}
                    disabled={loadingComponentId === component.componentId}
                    className="flex-shrink-0 text-xs font-medium flex items-center gap-1.5 transition-all duration-200"
                    style={{
                      color: loadingComponentId === component.componentId 
                        ? 'var(--pallium-text-secondary)' 
                        : 'var(--pallium-primary-purple)',
                      cursor: loadingComponentId === component.componentId ? 'default' : 'pointer'
                    }}
                    onMouseOver={(e) => {
                      if (loadingComponentId !== component.componentId) {
                        e.currentTarget.style.color = 'var(--pallium-primary-purple-dark)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (loadingComponentId !== component.componentId) {
                        e.currentTarget.style.color = 'var(--pallium-primary-purple)';
                      }
                    }}
                  >
                    {loadingComponentId === component.componentId ? (
                      <>
                        <svg 
                          className="animate-spin w-3 h-3" 
                          xmlns="http://www.w3.org/2000/svg" 
                          fill="none" 
                          viewBox="0 0 24 24"
                        >
                          <circle 
                            className="opacity-25" 
                            cx="12" 
                            cy="12" 
                            r="10" 
                            stroke="currentColor" 
                            strokeWidth="4"
                          />
                          <path 
                            className="opacity-75" 
                            fill="currentColor" 
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        <span>Loading...</span>
                      </>
                    ) : (
                      'View Details'
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ComponentDetailsList;

